<template>
  <div class="person-list-view">
    <!-- Header -->
    <div class="flex justify-between items-center mb-3">
      <h1 class="text-2xl font-bold">People</h1>
      <div class="flex ml-auto gap-2">
        <Button icon="pi pi-plus" label="Add Person" @click="addNewPerson" />
      </div>
    </div>

    <!-- Search Bar -->
    <Card class="mb-4">
      <template #content>
        <div class="flex gap-4">
          <InputText
            v-model="search"
            placeholder="Search by first or last name..."
            class="flex-1"
            @input="onSearchChange"
          />
        </div>
      </template>
    </Card>

    <!-- Tabs -->
    <div class="">
      <TabView v-model:activeIndex="activeTabIndex">
        <TabPanel value="0">
          <template #header>
            <div class="flex items-center gap-2">
              <span>All</span>
              <Badge :value="allCount" severity="info" />
            </div>
          </template>
        </TabPanel>
        <TabPanel value="1">
          <template #header>
            <div class="flex items-center gap-2">
              <span>Suppliers</span>
              <Badge :value="supplierCount" severity="success" />
            </div>
          </template>
        </TabPanel>
        <TabPanel value="2">
          <template #header>
            <div class="flex items-center gap-2">
              <span>Prospects</span>
              <Badge :value="prospectCount" severity="warning" />
            </div>
          </template>
        </TabPanel>
        <TabPanel value="3">
          <template #header>
            <div class="flex items-center gap-2">
              <span>Leads</span>
              <Badge :value="leadCount" severity="help" />
            </div>
          </template>
        </TabPanel>
        <TabPanel value="4">
          <template #header>
            <div class="flex items-center gap-2">
              <span>Suspects</span>
              <Badge :value="suspectCount" severity="danger" />
            </div>
          </template>
        </TabPanel>
      </TabView>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-8">
      <ProgressSpinner />
    </div>

    <!-- People List -->
    <div v-else class="space-y-4">
      <PersonCard
        v-for="person in people"
        :key="person.id"
        :person="person"
        @view="viewPerson"
        @edit="editPerson"
        @delete="handleDeletePerson"
        class="my-1"
      />
    </div>

    <!-- Empty State -->
    <div v-if="!loading && people.length === 0" class="text-center py-8">
      <i class="pi pi-users text-4xl text-gray-400 mb-4"></i>
      <h3 class="text-lg font-medium text-gray-600 mb-2">No people found</h3>
      <p class="text-gray-500">Try adjusting your search or create a new person.</p>
    </div>

    <!-- Load More Button -->
    <div v-if="!loading && people.length > 0 && hasMore" class="text-center mt-6">
      <Button label="Load More" icon="pi pi-refresh" outlined @click="loadMore" />
    </div>

    <!-- Person Dialog -->
    <PersonDialog
      :visible="personDialogVisible"
      :personData="dialogPerson"
      :isEditing="isEditing"
      @update:visible="personDialogVisible = $event"
      @person-saved="onPersonSaved"
      @cancel="onDialogCancel"
    />
  </div>
</template>

<script setup lang="ts">
import { onMounted, computed, ref, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'primevue/usetoast'
import { usePersonStore } from '@/stores/v2/person'
import { useSession } from '@/stores/authStore'
import { createPerson } from '@/factories/personFactory'
import type { Person } from '@/types/Person'

type Session = { tenantId?: string; yardId?: string; userId?: string }

// Components
import Badge from 'primevue/badge'
import Button from 'primevue/button'
import Card from 'primevue/card'
import InputText from 'primevue/inputtext'
import ProgressSpinner from 'primevue/progressspinner'
import TabView from 'primevue/tabview'
import TabPanel from 'primevue/tabpanel'
import PersonCard from '@/components/person/PersonCard.vue'
import PersonDialog from '@/components/person/PersonDialog.vue'

const router = useRouter()
const toast = useToast()
const personStore = usePersonStore()
const session = useSession()

// State
const people = ref<Person[]>([])
const loading = ref(false)
const search = ref('')
const pageSize = ref(50)
const hasMore = ref(true)

// Tab state
const activeTabIndex = ref<number>(0)

// Count state
const allCount = ref(0)
const supplierCount = ref(0)
const prospectCount = ref(0)
const leadCount = ref(0)
const suspectCount = ref(0)

// Debounce search
let searchTimeout: NodeJS.Timeout | null = null

// Dialog state
const personDialogVisible = ref(false)
const dialogPerson = ref<Partial<Person>>(createPerson())
const isEditing = ref(false)

// Function to update counts
async function updateCounts() {
  try {
    const search = await personStore.search()

    const allResult = await search.count()
    const supplierResult = await search.countSuppliers().count()
    const prospectResult = await search.countProspects().count()
    const leadResult = await search.countLeads().count()
    const suspectResult = await search.countSuspects().count()

    allCount.value = allResult
    supplierCount.value = supplierResult
    prospectCount.value = prospectResult
    leadCount.value = leadResult
    suspectCount.value = suspectResult
  } catch (error) {
    console.error('Error updating counts:', error)
  }
}

// Load people based on current tab and search
async function loadPeople() {
  loading.value = true
  try {
    const searchInstance = await personStore.search()
    let searchQuery = searchInstance

    // Apply tab filter first
    const tabIndex = activeTabIndex.value ?? 0
    if (tabIndex === 1) {
      // Suppliers tab
      searchQuery = searchQuery.bySupplierStatus()
    } else if (tabIndex === 2) {
      // Prospects tab
      searchQuery = searchQuery.byProspectStatus()
    } else if (tabIndex === 3) {
      // Leads tab
      searchQuery = searchQuery.byLeadStatus()
    } else if (tabIndex === 4) {
      // Suspects tab
      searchQuery = searchQuery.bySuspectStatus()
    }
    // All tab (tabIndex === 0) - no additional filtering needed

    // Apply search filter
    if (search.value.trim()) {
      const searchTerm = search.value.trim().toLowerCase()
      // Try both first name and last name prefix search
      searchQuery = searchQuery.prefixFirstName(searchTerm, pageSize.value)
    } else {
      // Default to recently updated
      searchQuery = searchQuery.recentlyUpdated(pageSize.value)
    }

    const results = await searchQuery.run()
    people.value = results
    hasMore.value = people.value.length === pageSize.value
  } catch (error) {
    console.error('Error loading people:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load people',
      life: 3000,
    })
  } finally {
    loading.value = false
  }
}

// Watch for search changes with debounce
function onSearchChange() {
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }
  searchTimeout = setTimeout(() => {
    loadPeople()
  }, 300)
}

// Watch for tab changes
watch(activeTabIndex, () => {
  loadPeople()
})

// Initialize
onMounted(async () => {
  await updateCounts()
  await nextTick()
  activeTabIndex.value = 0
  await loadPeople()
})

function loadMore() {
  pageSize.value += 20
  loadPeople()
}

// Person methods
function addNewPerson() {
  dialogPerson.value = createPerson({
    type: 'customer',
    status: 'customer',
    isVerified: false,
    isExempt: false,
    tags: [],
  })
  isEditing.value = false
  personDialogVisible.value = true
}

function viewPerson(person: Person) {
  if (!person?.id) return
  router.push(`/crm/people/${person.id}`)
}

function editPerson(person: Person) {
  if (!person?.id) return
  dialogPerson.value = { ...person }
  isEditing.value = true
  personDialogVisible.value = true
}

async function handleDeletePerson(person: Person) {
  if (!person?.id) return

  try {
    await personStore.remove(person.id)
    toast.add({
      severity: 'success',
      summary: 'Person Deleted',
      detail: `${person.firstName} ${person.lastName} has been deleted`,
      life: 3000,
    })
    await loadPeople()
    await updateCounts()
  } catch (error) {
    console.error('Error deleting person:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to delete person',
      life: 3000,
    })
  }
}

async function onPersonSaved(saved: Partial<Person>) {
  try {
    if (saved.id) {
      await personStore.update(saved.id, saved)
      toast.add({
        severity: 'success',
        summary: 'Person Updated',
        detail: `${saved.firstName ?? ''} ${saved.lastName ?? ''} has been updated`,
        life: 3000,
      })
    } else {
      const newId = await personStore.create(saved)
      toast.add({
        severity: 'success',
        summary: 'Person Created',
        detail: `${saved.firstName ?? 'New'} ${saved.lastName ?? 'Person'} has been created`,
        life: 3000,
      })
      // Optionally could use newId if needed later
    }
    await loadPeople()
    await updateCounts()
  } catch (error) {
    console.error('Error saving person:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to save person',
      life: 3000,
    })
  }
}

function onDialogCancel() {
  // No-op for now; dialog handles visibility via v-model
}
</script>

<style scoped>
.person-list-view {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem;
}

.p-card {
  border-radius: 0px;
}

/* Custom button styling for the outlined button */
:deep(.p-button-outlined) {
  border: 1px solid var(--p-primary-color);
  background: transparent;
  color: var(--p-primary-color);
  transition: all 0.2s ease-in-out;
}

:deep(.p-button-outlined:hover) {
  background: var(--p-primary-color);
  color: var(--p-primary-color-text);
  transform: scale(1.05);
}
</style>
